import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Input, Button, Typography, theme } from 'antd';
import { AnimatePresence, motion, useDragControls } from 'framer-motion';
import { IconReceipt2 } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { message } from '../services/toastMessage';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';
import { addInvoiceTaxInfo } from '../services/InvoiceService';
import { IInvoice } from '../interfaces/heavyDutyMachinery/IInvoice';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { CORAL } from '../theme/creamyTokens';

const { Text } = Typography;

interface AddInvoiceTaxInfoModalProps {
  open: boolean;
  invoice: IInvoice;
  onClose: () => void;
  onUpdated: (invoice: IInvoice) => void;
}

/**
 * RD 1619/2012 Art. 7.2 self-service: lets the guest add their own NIF and
 * fiscal address to an already-issued Factura Simplificada so they can
 * deduct it, without needing a brand new invoice (same InvoiceNumber, PDF
 * regenerated server-side -- see InvoicesController.AddTaxInfo). Same
 * mobile creamy-sheet / desktop plain-Modal split as ReportModal/
 * HostCancelBookingModal, for the same reason those already use it.
 */
const AddInvoiceTaxInfoModal: React.FC<AddInvoiceTaxInfoModalProps> = ({ open, invoice, onClose, onUpdated }) => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { token } = theme.useToken();
  const [taxId, setTaxId] = useState('');
  const [address, setAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const dragControls = useDragControls();

  const canSubmit = !!taxId.trim() && !!address.trim();

  const handleClose = () => {
    setTaxId('');
    setAddress('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const updated = await addInvoiceTaxInfo(invoice.id, taxId.trim(), address.trim());
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_ADD_TAX_ID_SUCCESS));
      onUpdated(updated);
      handleClose();
    } catch {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_ADD_TAX_ID_ERROR));
    } finally {
      setSubmitting(false);
    }
  };

  const fields = (
    <>
      <Input
        value={taxId}
        onChange={(e) => setTaxId(e.target.value)}
        placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_TAX_ID_PLACEHOLDER)}
        maxLength={20}
      />
      <Input
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_TAX_ADDRESS_PLACEHOLDER)}
        maxLength={300}
      />
    </>
  );

  if (isMobile) {
    return createPortal(
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="invoice-tax-info-sheet-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleClose}
              style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.45)' }}
            />
            <motion.div
              key="invoice-tax-info-sheet"
              drag="y"
              dragControls={dragControls}
              dragListener={false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.5 }}
              dragSnapToOrigin
              onDragEnd={(_, info) => {
                if (info.offset.y > 80 || info.velocity.y > 500) handleClose();
              }}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              style={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2101,
                width: '100%',
                maxWidth: 480,
                margin: '0 auto',
                background: token.colorBgContainer,
                borderRadius: '20px 20px 0 0',
                boxShadow: '0 -8px 24px rgba(0,0,0,0.16)',
                padding: '10px 20px calc(20px + env(safe-area-inset-bottom, 0px))',
              }}
            >
              <div
                onPointerDown={(e) => dragControls.start(e)}
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: token.colorBorder,
                  margin: '0 auto 16px',
                  cursor: 'grab',
                  touchAction: 'none',
                }}
              />
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
                <span
                  style={{
                    display: 'flex',
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: token.colorFillTertiary,
                    color: CORAL,
                  }}
                >
                  <IconReceipt2 size={20} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <Text strong style={{ display: 'block', fontSize: 16 }}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_ADD_TAX_ID_MODAL_TITLE)}
                  </Text>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                <Input
                  value={taxId}
                  onChange={(e) => setTaxId(e.target.value)}
                  placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_TAX_ID_PLACEHOLDER)}
                  maxLength={20}
                  style={{ borderRadius: 999 }}
                />
                <Input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_TAX_ADDRESS_PLACEHOLDER)}
                  maxLength={300}
                  style={{ borderRadius: 999 }}
                />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <Button
                  block
                  style={{ borderRadius: 999, border: `1px solid ${token.colorBorder}`, background: 'transparent' }}
                  onClick={handleClose}
                >
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_ADD_TAX_ID_CANCEL)}
                </Button>
                <Button
                  type="primary"
                  block
                  loading={submitting}
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                  style={{ borderRadius: 999 }}
                >
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_ADD_TAX_ID_SUBMIT)}
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );
  }

  return (
    <Modal
      title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_ADD_TAX_ID_MODAL_TITLE)}
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_ADD_TAX_ID_SUBMIT)}
      okButtonProps={{ disabled: !canSubmit }}
      confirmLoading={submitting}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{fields}</div>
    </Modal>
  );
};

export default AddInvoiceTaxInfoModal;
